'use client';

import * as React from 'react';
import { useHotelMapModal } from '@/app/components/hotel-detail/HotelMapModalProvider';
import { sendMetricIncrement } from '@/app/components/hotel-detail/metrics';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import type { Brand } from '@/domainLogic/index.ts';
import BasicModalHeader from '@/app/components/search/components/Modal/LayoutElements/BasicModalHeader';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import HotelMap from '@/app/components/hotel-detail/HotelMap';
import { Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { sumClasses } from '@/app/components/search/utils/misc.ts';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';

type HotelMapModalProps = React.HTMLAttributes<HTMLElement>;

const handleMapClick = (pageType: Pages) => {
  sendMetricIncrement(`app.map.${pageType}.dynamic_map_click`);
};

export default function HotelMapModal(props: HotelMapModalProps) {
  const { isShowingMap, closeMap, showMap } = useHotelMapModal();
  const { pageType } = useInitialParams();
  const { localization } = usePageWrapperData();
  const { isEvoLayout } = useCommonClient();
  const { data: staticData } = useFetchHotelStatic();

  // MPH-981: the Evo map has no title bar — its close button floats over the map (HotelMap), and the
  // hotel name stays as the dialog's accessible name only.
  const header = isEvoLayout
    ? undefined
    : {
        sticky: true,
        element: (
          <BasicModalHeader handleClose={closeMap}>
            <Text
              variant="Title4"
              css={'padding: 0 46px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;'}
            >
              {staticData?.name}
            </Text>
          </BasicModalHeader>
        ),
      };

  return (
    <OpenXModal
      isOpen={isShowingMap ?? false}
      isMobile={true}
      brand={localization.brand as Brand}
      onClose={closeMap}
      onCloseByBrowserHistory={closeMap}
      onOpenByBrowserHistory={showMap}
      fullScreen={true}
      disableAnimation={true}
      className={sumClasses(['hotel-map-modal', isEvoLayout ? 'hotel-map-modal-evo' : ''])}
      id="hotel-map-modal"
      {...(staticData?.name ? { 'aria-label': staticData.name } : {})}
      bodyBackgroundColor="backgroundPage"
      header={header}
    >
      <HotelMap {...props} coordinates={staticData?.coordinates} onClick={() => handleMapClick(pageType)} />
    </OpenXModal>
  );
}
